import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/base/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import { aboutImage, aboutImageAlt, milestones, principles, site, stats, testimonials } from "@/mocks/site";

const crumbs = [
  { label: "首页", path: "/" },
  { label: "关于我们", path: "/about" },
];

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "一个整理者，不是搬运号",
    desc: "我们不追着热点转发，而是等材料齐了再动笔，把原帖、截图、回应重新拼回一件完整的事。",
  },
  {
    icon: "ri-group-line",
    title: "面向普通吃瓜的人",
    desc: "不假设你熟悉圈内黑话，也不默认你知道前情。每一篇都按「第一次听说」的标准写清楚。",
  },
  {
    icon: "ri-history-line",
    title: "记录，而不只是报道",
    desc: "事件过去后，页面依然保留完整时间线，方便你日后回看，也方便查证当时的说法。",
  },
];

export default function About() {
  return (
    <SiteLayout>
      <Seo
        title="关于我们 | 网红大瓜 - 瓜田情报站的整理原则与定位"
        description="网红大瓜是一个专注网红圈猛料整理的内容站，坚持原帖优先、时间线完整、补齐双方说法。了解我们的品牌定位、内容原则、发展理念与联系方式。"
        keywords={["网红大瓜", "关于网红大瓜", "网红大瓜内容原则", "瓜田情报站", "猛料整理"]}
        canonicalPath="/about"
        image={aboutImage}
        imageAlt={aboutImageAlt}
        breadcrumbs={crumbs}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于我们 - 网红大瓜",
            url: "https://chengshunjt.cn/about",
            inLanguage: "zh-CN",
            description: site.description,
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: site.name,
            url: "https://chengshunjt.cn/",
            email: site.email,
            foundingDate: site.since,
          },
        ]}
      />

      <section className="border-b border-background-200 bg-foreground-950">
        <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
          <div className="mb-4 [&_a]:text-background-300 [&_span]:text-background-50 [&_ol]:text-background-400">
            <Breadcrumb items={crumbs} />
          </div>
          <span className="mb-3 inline-flex items-center gap-2 text-xs font-medium tracking-[0.25em] text-accent-400">
            <i className="ri-information-line" />
            ABOUT {site.englishName.toUpperCase()}
          </span>
          <h1 className="mb-4 max-w-3xl font-heading text-3xl font-black text-background-50 md:text-5xl">
            关于{site.name}：我们如何整理每一条大瓜
          </h1>
          <p className="max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">
            {site.name}（{site.tagline}）是一个专注网红圈信息整理的内容站。我们相信，吃瓜的重点不是站队，
            而是先把事情看清楚。所以从第一天起，我们就只做一件事：把碎片拼回完整。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 py-16 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <div className="relative h-64 w-full overflow-hidden rounded-lg bg-background-100 md:h-96">
              <img
                src={aboutImage}
                alt={aboutImageAlt}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="OUR STORY" title="品牌介绍" />
            <div className="space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              <p>
                {site.name}起步于 {site.since} 年的一个共享收藏夹。最早只是几个人在群里转发链接，
                后来发现同一个事件会被反复讨论，但每次都要重新解释前情，效率很低。
              </p>
              <p>
                于是我们开始把链接整理成条目：谁先发的、什么时候发的、有没有回应、后续怎么样了。
                这些条目越来越多，慢慢就长成了今天的{site.name}——一个把散落在各处的猛料归到一起看的情报站。
              </p>
              <p>
                截至目前，我们累计归档了 {site.coverCount}+ 条瓜帖，覆盖重磅爆料、惊天大瓜、猛料曝光、
                年度大瓜与超级大瓜五个方向。所有内容都基于公开渠道信息整理，人物统一使用化名。
              </p>
            </div>
            <dl className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
              {stats.map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg border border-background-200 bg-background-100 p-4"
                >
                  <dt className="font-heading text-xl font-bold text-primary-600 md:text-2xl">
                    {item.value}
                  </dt>
                  <dd className="mt-1 text-xs text-foreground-600">{item.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1400px] px-4 py-16 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="POSITIONING"
            title="网站定位"
            description="我们清楚自己该做什么，也清楚自己不该做什么。"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {positioning.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="mb-2 font-heading text-lg font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 py-16 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="PRINCIPLES"
          title="内容原则"
          description="这四条原则决定了我们写什么、怎么写，也决定了我们什么时候选择先不写。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {principles.map((item, index) => (
            <div
              key={item.title}
              className="flex gap-5 rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${item.icon} text-2xl`} />
              </span>
              <div>
                <h3 className="mb-1.5 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <span className="text-xs text-foreground-400">
                    0{index + 1}
                  </span>
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1400px] px-4 py-16 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="MILESTONES"
            title="发展理念与历程"
            description="我们走得不算快，但每一步都围绕同一件事：让信息更好读。"
          />
          <ol className="relative space-y-8 border-l border-background-300 pl-6 md:grid md:grid-cols-4 md:gap-6 md:space-y-0 md:border-l-0 md:border-t md:pl-0 md:pt-8">
            {milestones.map((item) => (
              <li key={item.year} className="relative md:pt-6">
                <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full border-2 border-background-100 bg-primary-500 md:-top-[21px] md:left-1/2 md:-translate-x-1/2" />
                <span className="mb-2 block font-heading text-lg font-bold text-primary-600 md:text-center">
                  {item.year}
                </span>
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="mb-1.5 font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 py-16 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="TESTIMONIALS"
          title="瓜友怎么说"
          description="这些话来自长期关注我们的读者，也是我们继续更新的理由。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {testimonials.map((item) => (
            <figure
              key={item.name}
              className="flex h-full flex-col justify-between rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <blockquote className="mb-4 text-sm leading-relaxed text-foreground-700">
                “{item.quote}”
              </blockquote>
              <figcaption className="flex items-center gap-2 text-xs text-foreground-500">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-user-3-line text-sm" />
                </span>
                <span className="font-medium text-foreground-800">{item.name}</span>
                <span>· {item.role}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-4 pb-4 md:px-6">
        <div className="rounded-lg border border-primary-200 bg-primary-50 px-6 py-10 md:px-12 md:py-12">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="mb-2 font-heading text-2xl font-bold text-foreground-950">
                有瓜想投，或有内容需要更正？
              </h2>
              <p className="text-sm leading-relaxed text-foreground-700">
                无论是投稿、纠错还是内容下架请求，都可以通过联系页面告诉我们，编辑部会尽快处理。
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-mail-send-line" />
                联系我们
              </Link>
              <a
                href={`mailto:${site.email}`}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                {site.email}
              </a>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}